import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLeagueStrict } from "@/lib/league-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import {
  listMyEntries,
  updateEntry,
  type EntryRow,
} from "@/lib/entries.functions";
import { EntryForm } from "@/components/EntryForm";

export const Route = createFileRoute("/_app/$league/entries/$entryId/edit")({
  component: EditEntryPage,
  head: () => ({
    meta: [{ title: "Edit bracket — Bracket Goblin" }],
  }),
});

function EditEntryPage() {
  const league = useLeagueStrict();
  const { entryId } = Route.useParams();
  const navigate = useNavigate();
  const listFn = useAuthedServerFn(listMyEntries);
  const updateFn = useAuthedServerFn(updateEntry);

  const [entry, setEntry] = useState<EntryRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listFn({ data: { leagueId: league.id } });
        const found = res.entries.find((e) => e.id === entryId);
        if (!cancelled) {
          if (!found) {
            toast.error("Bracket not found.");
            navigate({ to: "/$league", params: { league: league.slug } });
            return;
          }
          setEntry(found);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [league.id, league.slug, entryId, listFn, navigate]);

  if (loading || !entry) {
    return (
      <main className="mx-auto max-w-xl px-4 py-16 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
        Loading…
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-xl px-4 py-6 sm:py-10">
      <Link
        to="/$league"
        params={{ league: league.slug }}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to league
      </Link>
      <h1 className="mt-3 mb-6 font-display text-2xl font-bold text-foreground sm:text-3xl">
        Edit bracket
      </h1>
      <EntryForm
        initial={{
          bracketName: entry.bracketName,
          entryType: entry.entryType,
          proxyDisplayName: entry.proxyDisplayName ?? "",
          relationship: entry.relationship ?? "",
        }}
        submitLabel="Save changes"
        busy={busy}
        allowProxy={(league.maxEntriesPerMember ?? 1) > 1}
        onCancel={() => navigate({ to: "/$league", params: { league: league.slug } })}
        onSubmit={async (values) => {
          setBusy(true);
          try {
            await updateFn({
              data: {
                entryId: entry.id,
                bracketName: values.bracketName,
                entryType: values.entryType,
                proxyDisplayName: values.proxyDisplayName || undefined,
                relationship: values.relationship || undefined,
              },
            });
            toast.success("Bracket updated.");
            navigate({ to: "/$league", params: { league: league.slug } });
          } finally {
            setBusy(false);
          }
        }}
      />
    </main>
  );
}
